import React, { useEffect, useState, useCallback } from 'react';
import { 
  Tabs, 
  Tab, 
  Box, 
  Typography, 
  Button, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Switch, 
  FormControlLabel,
  Card,
  CardContent,
  Paper,
  Chip,
  InputAdornment,
  useTheme,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getProcedureCodesPaginated, addProcedureCode, updateProcedureCode, deactivateProcedureCode } from '../../services/procedureCodesService';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.success.light : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 500,
  fontSize: '0.75rem',
}));

const codeTypes = [
  { key: 'snomed', label: 'SNOMED' },
];

const defaultForm = {
  procedure_code: '',
  procedure_code_type: 'SNOMED',
  name: '',
  active: true,
  notes: '',
};

const ClinicalTermsCodesPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    hasMore: true,
    nextCursor: null,
    total: null
  });
  

  const fetchCodes = useCallback(async (startAfter = null, append = false) => {
    setLoading(true);
    try {
      // Only fetch codes for the current tab type with pagination
      const codeType = codeTypes[tab].label;
      console.log(`Fetching ${codeType} codes with pagination`);
      
      // Use paginated query to limit data transfer
      const result = await getProcedureCodesPaginated({
        procedureCodeType: codeType,
        startAfter,
        pageSize: 50, // Use reasonable limit for better UX
        activeOnly: true
      });
      
      // Ensure result is valid and has the expected structure
      if (!result || typeof result !== 'object') {
        console.warn('Invalid result from getProcedureCodesPaginated:', result);
        setCodes([]);
        return;
      }
      
      const procedureCodes = result.procedureCodes || [];
      
      if (append && startAfter) {
        // Append to existing codes for pagination
        setCodes(prevCodes => [...prevCodes, ...procedureCodes]);
      } else {
        // First load or refresh
        setCodes(procedureCodes);
      }
      
      // Update pagination state
      setPagination({
        hasMore: result.pagination?.hasMore || false,
        nextCursor: result.pagination?.nextCursor || null,
        total: result.pagination?.total || null
      });
      
      console.log(`✅ Fetched ${procedureCodes.length} ${codeType} codes`);
    } catch (error) {
      console.error('Error fetching codes:', error);
      setCodes([]);
      setPagination({ hasMore: false, nextCursor: null, total: null });
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleOpenDialog = (code) => {
    if (code) {
      setForm(code);
      setEditId(code.id);
    } else {
      setForm(defaultForm);
      setEditId(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setForm(defaultForm);
    setEditId(null);
  };

  const handleSave = async () => {
    if (editId) {
      await updateProcedureCode(editId, form);
    } else {
      await addProcedureCode(form);
    }
    handleCloseDialog();
    fetchCodes();
  };

  const handleDeactivate = async (id) => {
    await deactivateProcedureCode(id);
    fetchCodes();
  };

  // Since we're already fetching by type, just filter by search term
  const filteredCodes = codes.filter(c => 
    !search || (
      c.procedure_code?.toLowerCase().includes(search.toLowerCase()) ||
      c.name?.toLowerCase().includes(search.toLowerCase())
    )
  );

  // Load more functionality
  const handleLoadMore = () => {
    if (pagination.hasMore && pagination.nextCursor) {
      fetchCodes(pagination.nextCursor, true);
    }
  };

  // Reset codes when search or tab changes
  useEffect(() => {
    setCodes([]);
    setPagination({ hasMore: true, nextCursor: null, total: null });
    fetchCodes();
  }, [search, tab, fetchCodes]);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Clinical Terms Codes Management
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Manage SNOMED and other clinical terminology codes
              </Typography>
            </Box>
            <Button 
              variant="contained"
              onClick={() => navigate('/admin/codes')}
              startIcon={<ArrowBack />}
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Back to Codes
            </Button>
          </Box>
        </CardContent>
      </HeaderCard>

      <Box sx={{ mb: 3 }}>
        <StyledTabs value={tab} onChange={(_, v) => setTab(v)}>
          {codeTypes.map((ct) => (
            <StyledTab key={ct.key} label={ct.label} />
          ))}
        </StyledTabs>
      </Box>

      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <TextField
              label="Search codes"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button 
              variant="contained" 
              onClick={() => handleOpenDialog()}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Add New Code
            </Button>
          </Box>

          {!loading && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredCodes.length} {codeTypes[tab].label.toLowerCase()} codes
                {search && ` matching "${search}"`}
                {pagination.hasMore && ` (${pagination.nextCursor ? 'Load more available' : 'Initial load'})`}
              </Typography>
            </Box>
          )}

          <StyledTableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Code</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        Loading clinical terms codes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {search ? 'No codes found matching your search.' : 'No codes available for this type.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCodes.map(code => (
                    <TableRow 
                      key={code.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover 
                        } 
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{code.procedure_code}</TableCell>
                      <TableCell>{code.name}</TableCell>
                      <TableCell>
                        <StatusChip 
                          active={code.active}
                          label={code.active ? 'Active' : 'Inactive'}
                          icon={code.active ? <CheckCircleIcon /> : <CancelIcon />}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <ActionButton 
                            size="small" 
                            onClick={() => handleOpenDialog(code)}
                            startIcon={<EditIcon />}
                            variant="outlined"
                            color="primary"
                          >
                            Edit
                          </ActionButton>
                          {code.active && (
                            <ActionButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleDeactivate(code.id)}
                              startIcon={<DeleteIcon />}
                              variant="outlined"
                            >
                              Deactivate
                            </ActionButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
          
          {pagination.hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading || !pagination.nextCursor}
                startIcon={loading ? <CircularProgress size={16} /> : <ExpandMoreIcon />}
                sx={{ borderRadius: '12px', textTransform: 'none' }}
              >
                {loading ? 'Loading...' : 'Load More Codes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editId ? 'Edit' : 'Add New'} SNOMED Code
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 1 }}>
            <TextField 
              label="Code" 
              fullWidth 
              value={form.procedure_code} 
              onChange={e => setForm(f => ({ ...f, procedure_code: e.target.value }))} 
            />
            <TextField 
              label="Name" 
              fullWidth 
              value={form.name} 
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} 
            />
          </Box>
          <TextField 
            label="Notes" 
            fullWidth 
            multiline
            rows={3}
            value={form.notes} 
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} 
            sx={{ mt: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
            <FormControlLabel 
              control={
                <Switch 
                  checked={form.active} 
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} 
                />
              } 
              label="Active" 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              borderRadius: '8px',
              px: 3
            }}
          >
            {editId ? 'Update' : 'Create'} Code
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicalTermsCodesPage; 